<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ActivityLogsAPI from '../../../../api/activityLogs';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const records = ref([]);
const meta = ref({ currentPage: 1, totalEntries: 0, perPage: 25 });
const isLoading = ref(false);

const filterDateGte = ref('');
const filterDateLte = ref('');
const filterAgentId = ref('');
const filterKeyword = ref('');

const agentList = computed(() => getters['agents/getAgents'].value);

const fetchActivityLogs = async (page = 1) => {
  isLoading.value = true;
  try {
    const response = await ActivityLogsAPI.get({
      page,
      createdAtGte: filterDateGte.value,
      createdAtLte: filterDateLte.value,
      agentId: filterAgentId.value,
      q: filterKeyword.value,
    });

    records.value = response.data.data.payload;
    meta.value = {
      currentPage: response.data.data.meta.current_page,
      totalEntries: response.data.data.meta.total_entries,
      perPage: response.data.data.meta.per_page,
    };
  } catch (error) {
    const errorMessage = error?.message || t('ACTIVITY_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isLoading.value = false;
  }
};

const onPageChange = page => {
  fetchActivityLogs(page);
};

const applyFilters = () => {
  fetchActivityLogs(1);
};

const resetFilters = () => {
  filterDateGte.value = '';
  filterDateLte.value = '';
  filterAgentId.value = '';
  filterKeyword.value = '';
  fetchActivityLogs(1);
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchActivityLogs(1);
});

const tableHeaders = computed(() => {
  return [
    t('ACTIVITY_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
    t('ACTIVITY_LOGS.LIST.TABLE_HEADER.TIME'),
    t('ACTIVITY_LOGS.LIST.TABLE_HEADER.CONVERSATION'),
  ];
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('ACTIVITY_LOGS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('ACTIVITY_LOGS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('ACTIVITY_LOGS.HEADER')"
        :description="$t('ACTIVITY_LOGS.DESCRIPTION')"
        :link-text="$t('ACTIVITY_LOGS.LEARN_MORE')"
        feature-name="activity_logs"
      />

      <!-- Filtros -->
      <div
        class="flex items-start gap-4 mt-4 bg-n-alpha-1 rounded-md p-4 flex-wrap"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{
            $t('ACTIVITY_LOGS.FILTERS.DATE_FROM')
          }}</label>
          <input
            v-model="filterDateGte"
            type="date"
            class="h-8 px-2 text-sm border border-n-strong rounded-md bg-n-alpha-0 text-n-slate-12 focus:ring-1 focus:ring-woot-500 outline-none"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{
            $t('ACTIVITY_LOGS.FILTERS.DATE_TO')
          }}</label>
          <input
            v-model="filterDateLte"
            type="date"
            class="h-8 px-2 text-sm border border-n-strong rounded-md bg-n-alpha-0 text-n-slate-12 focus:ring-1 focus:ring-woot-500 outline-none"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{
            $t('ACTIVITY_LOGS.FILTERS.AGENT')
          }}</label>
          <select
            v-model="filterAgentId"
            class="h-8 px-2 text-sm border border-n-strong rounded-md bg-n-alpha-0 text-n-slate-12 focus:ring-1 focus:ring-woot-500 outline-none w-48"
          >
            <option value="">
              {{ $t('ACTIVITY_LOGS.FILTERS.ALL_AGENTS') }}
            </option>
            <option
              v-for="agent in agentList"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{
            $t('ACTIVITY_LOGS.FILTERS.SEARCH')
          }}</label>
          <input
            v-model="filterKeyword"
            type="text"
            :placeholder="$t('ACTIVITY_LOGS.FILTERS.SEARCH_PLACEHOLDER')"
            class="h-8 px-2 text-sm border border-n-strong rounded-md bg-n-alpha-0 text-n-slate-12 focus:ring-1 focus:ring-woot-500 outline-none w-48"
            @keyup.enter="applyFilters"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium invisible select-none">&nbsp;</label>
          <div class="flex items-center gap-2 h-8">
            <button
              class="h-8 px-3 bg-woot-500 text-white text-sm font-medium rounded-md hover:bg-woot-600 transition-colors"
              @click="applyFilters"
            >
              {{ $t('ACTIVITY_LOGS.FILTERS.APPLY') }}
            </button>
            <button
              class="h-8 px-3 bg-n-alpha-2 text-n-slate-11 text-sm font-medium rounded-md hover:bg-n-alpha-3 transition-colors"
              @click="resetFilters"
            >
              {{ $t('ACTIVITY_LOGS.FILTERS.RESET') }}
            </button>
          </div>
        </div>
      </div>
    </template>
    <template #body>
      <div class="flex flex-col mt-4">
        <BaseTable :headers="tableHeaders" :items="records">
          <template #row="{ items }">
            <BaseTableRow v-for="log in items" :key="log.id" :item="log">
              <template #default>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-12">
                    {{ log.content }}
                  </span>
                </BaseTableCell>

                <BaseTableCell>
                  <span
                    class="text-body-main text-n-slate-11 whitespace-nowrap"
                  >
                    {{
                      messageTimestamp(log.created_at, 'MMM dd, yyyy hh:mm a')
                    }}
                  </span>
                </BaseTableCell>

                <BaseTableCell>
                  <span
                    class="text-body-main text-woot-500 hover:underline cursor-pointer"
                    @click="
                      $router.push(
                        `/app/accounts/${$route.params.accountId}/conversations/${log.conversation_display_id}`
                      )
                    "
                  >
                    {{ `#${log.conversation_display_id}` }}
                  </span>
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
        <PaginationFooter
          v-if="meta.totalEntries > meta.perPage"
          :current-page="Number(meta.currentPage)"
          :total-items="meta.totalEntries"
          :items-per-page="meta.perPage"
          class="!px-0"
          @update:current-page="onPageChange"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
